import './CapacityPage.css';

import React, { useEffect, useMemo, useState } from 'react';

import http from '../../../api/http';
import PageHero from '../../../components/page/PageHero';
import RecordSection from '../../../components/view/RecordSection';

const DISPLAY_LIMIT = 100;
const MATRIX_STRATEGY_LIMIT = 30;

const CapacityPage = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;

    const loadCapacity = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await http.get('/capacity');
        if (!data?.totals || !data?.capacity || !data?.alignment) {
          throw new Error('The capacity endpoint returned an incomplete snapshot.');
        }
        if (active) setSnapshot(data);
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load capacity data.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadCapacity();
    return () => { active = false; };
  }, [reloadKey]);

  const matrix = useMemo(() => new Map(
    (snapshot?.alignment?.matrix || []).map((cell) => [`${cell.organizationId}:${cell.strategyId}`, cell])
  ), [snapshot]);
  const maximumCellLoad = Math.max(1, ...(snapshot?.alignment?.matrix || []).map((cell) => cell.load));

  const refresh = () => setReloadKey((value) => value + 1);
  const limitedRows = (rows) => (Array.isArray(rows) ? rows.slice(0, DISPLAY_LIMIT) : []);

  return (
    <div className="stack capacity-page">
      <PageHero
        title="Capacity & org alignment"
        subtitle="See where open work is staffed, how urgent it is, and which strategies each organization supports."
        actions={<button className="button-secondary" type="button" onClick={refresh} disabled={loading}>{loading ? 'Refreshing...' : 'Refresh'}</button>}
      />

      {loading && !snapshot ? <p>Loading capacity snapshot...</p> : null}
      {error ? <p className="error-text" role="alert">{error}</p> : null}

      {snapshot ? (
        <>
          <div className="card-grid metrics-grid capacity-summary">
            <div className="card metric-card"><span className="hero-stat-label">Open tasks</span><strong>{snapshot.totals.openTasks}</strong></div>
            <div className="card metric-card"><span className="hero-stat-label">Weighted load</span><strong>{snapshot.totals.weightedLoad} pts</strong></div>
            <div className="card metric-card"><span className="hero-stat-label">Unassigned tasks</span><strong>{snapshot.totals.unassignedTasks}</strong></div>
            <div className="card metric-card"><span className="hero-stat-label">Unstaffed strategies</span><strong>{snapshot.totals.unstaffedStrategies}</strong></div>
          </div>

          <RecordSection title="Organization × strategy alignment" className="capacity-alignment">
            <p className="small-muted">Each cell shows staffed open tasks and due-date-weighted load. Color intensity is relative within this snapshot, not a capacity limit.</p>
            {snapshot.alignment.organizations.length && snapshot.alignment.strategies.length ? (
              <div className="table-scroll capacity-matrix-scroll">
                <table className="data-table capacity-matrix">
                  <thead>
                    <tr>
                      <th scope="col">Organization</th>
                      {snapshot.alignment.strategies.slice(0, MATRIX_STRATEGY_LIMIT).map((strategy) => (
                        <th scope="col" key={strategy.id}>{strategy.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {snapshot.alignment.organizations.slice(0, DISPLAY_LIMIT).map((organization) => (
                      <tr key={organization.id}>
                        <th scope="row">{organization.label}</th>
                        {snapshot.alignment.strategies.slice(0, MATRIX_STRATEGY_LIMIT).map((strategy) => {
                          const cell = matrix.get(`${organization.id}:${strategy.id}`);
                          return (
                            <td
                              className={cell ? 'capacity-matrix-cell' : ''}
                              key={strategy.id}
                              style={cell ? { '--heat-intensity': `${Math.max(5, Math.round((cell.load / maximumCellLoad) * 32))}%` } : undefined}
                              title={cell ? `${cell.openTasks} open tasks; ${cell.load} weighted load points` : undefined}
                            >
                              {cell ? <><strong>{cell.openTasks}</strong><span>{cell.load} pts</span></> : <span className="small-muted">—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="small-muted">No organizations or strategies are available to align.</p>}
            {snapshot.alignment.organizations.length > DISPLAY_LIMIT
              || snapshot.alignment.strategies.length > MATRIX_STRATEGY_LIMIT
              ? <p className="small-muted">The matrix is limited to the first {DISPLAY_LIMIT} organizations and {MATRIX_STRATEGY_LIMIT} strategies.</p>
              : null}
          </RecordSection>

          <div className="card-grid two-col capacity-load-grid">
            <RecordSection title="Load by person" className="capacity-load">
              <div className="table-scroll">
                <table className="data-table">
                  <thead><tr><th scope="col">Person</th><th scope="col">Active jobs</th><th scope="col">Open tasks</th><th scope="col">Load</th></tr></thead>
                  <tbody>
                    {limitedRows(snapshot.capacity.people).map((person) => (
                      <tr key={person.id}><th scope="row">{person.label}</th><td>{person.activeJobs}</td><td>{person.openTasks}</td><td>{person.load} pts</td></tr>
                    ))}
                    {!snapshot.capacity.people.length ? <tr><td colSpan="4">No people found.</td></tr> : null}
                  </tbody>
                </table>
              </div>
              {snapshot.capacity.people.length > DISPLAY_LIMIT ? <p className="small-muted">Showing the first {DISPLAY_LIMIT} people.</p> : null}
            </RecordSection>

            <RecordSection title="Load by position" className="capacity-load">
              <div className="table-scroll">
                <table className="data-table">
                  <thead><tr><th scope="col">Position</th><th scope="col">Organization</th><th scope="col">Active jobs</th><th scope="col">Open tasks</th><th scope="col">Load</th></tr></thead>
                  <tbody>
                    {limitedRows(snapshot.capacity.positions).map((position) => (
                      <tr key={position.id}><th scope="row">{position.label}</th><td>{position.organization || '—'}</td><td>{position.activeJobs}</td><td>{position.openTasks}</td><td>{position.load} pts</td></tr>
                    ))}
                    {!snapshot.capacity.positions.length ? <tr><td colSpan="5">No positions found.</td></tr> : null}
                  </tbody>
                </table>
              </div>
              {snapshot.capacity.positions.length > DISPLAY_LIMIT ? <p className="small-muted">Showing the first {DISPLAY_LIMIT} positions.</p> : null}
            </RecordSection>
          </div>

          <div className="card-grid two-col">
            <RecordSection title="Unassigned open work" className="capacity-exceptions">
              {snapshot.alignment.unassignedTasks.length ? (
                <>
                  <ul className="capacity-exception-list">
                    {limitedRows(snapshot.alignment.unassignedTasks).map((task) => (
                      <li key={task.id}>
                        <strong>{task.title}</strong>
                        <span>{task.reasons.join('; ')} · {task.load} pts</span>
                        <span>{task.strategy || 'No linked strategy'}{task.dueDate ? ` · Due ${task.dueDate}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                  {snapshot.alignment.unassignedTasks.length > DISPLAY_LIMIT ? <p className="small-muted">Showing the first {DISPLAY_LIMIT} unassigned tasks.</p> : null}
                </>
              ) : <p className="small-muted">All open tasks have an active person, position, and organization assignment.</p>}
            </RecordSection>

            <RecordSection title="Orphan work" className="capacity-exceptions">
              {snapshot.alignment.orphanWork.length ? (
                <>
                  <ul className="capacity-exception-list">
                    {limitedRows(snapshot.alignment.orphanWork).map((task) => (
                      <li key={task.id}><strong>{task.title}</strong><span>{task.reason}</span></li>
                    ))}
                  </ul>
                  {snapshot.alignment.orphanWork.length > DISPLAY_LIMIT ? <p className="small-muted">Showing the first {DISPLAY_LIMIT} orphan tasks.</p> : null}
                </>
              ) : <p className="small-muted">All open tasks are linked to a Directional Strategy.</p>}
            </RecordSection>

            <RecordSection title="Unstaffed strategies" className="capacity-exceptions">
              {snapshot.alignment.unstaffedStrategies.length ? (
                <ul className="capacity-exception-list">
                  {snapshot.alignment.unstaffedStrategies.map((strategy) => (
                    <li key={strategy.id}>
                      <strong>{strategy.label}</strong>
                      <span>{strategy.openTasks} open {strategy.openTasks === 1 ? 'task' : 'tasks'} without staffed work.</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="small-muted">Every strategy with open tasks has at least one staffed task.</p>}
            </RecordSection>
          </div>

          <p className="small-muted">
            Snapshot: {new Date(snapshot.asOf).toLocaleString()}. Load weights: overdue {snapshot.weights.overdue},
            due within 7 days {snapshot.weights.dueWithinSevenDays}, later {snapshot.weights.later},
            no due date {snapshot.weights.noDueDate} points. {snapshot.weights.description}
          </p>
        </>
      ) : null}
    </div>
  );
};

export default CapacityPage;
